import { cva } from 'class-variance-authority'
import React, { useMemo } from 'react'
import { FormattedDate, FormattedTime } from 'react-intl'

import { cn } from '../../lib/utils'
import { Avatar } from '../avatar'
import { Card, CardContent, CardTitle } from '../card'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '../carousel'

function Messages({ className, ...props }: React.ComponentProps<'div'>) {
  return <div className={cn(className, 'flex flex-col gap-6')} {...props}></div>
}

export const buildMessageClassName = cva('flex flex-col gap-2 max-w-full', {
  variants: {
    direction: {
      incoming: 'pr-8 mr-auto',
      outgoing: 'pl-8 ml-auto items-end',
      none: 'mr-auto',
    },
  },
})

const buildMessageCardClassName = cva('py-4 gap-2 max-w-full', {
  variants: {
    variant: {
      secondary: 'border-transparent bg-secondary',
      outline: '',
      tertiary: 'border-transparent bg-tertiary-foreground',
    },
    headerImage: {
      true: 'pt-0 overflow-hidden pb-0',
    },
    headerImageAndContent: {
      true: 'pt-0 overflow-hidden',
    },
  },
})

const buildTextAlignClassName = cva('', {
  variants: {
    direction: {
      incoming: '',
      outgoing: 'text-right',
      none: '',
    },
  },
})

type MessageProps = React.ComponentProps<typeof Card> & {
  direction: 'incoming' | 'outgoing' | 'none'
  variant?: 'secondary' | 'outline' | 'tertiary'
}

const DEFAULT_VARIANT_MAP: Record<MessageProps['direction'], MessageProps['variant']> = {
  incoming: 'secondary',
  outgoing: 'outline',
  none: undefined,
}

function Message({ children, className, direction, variant, ...props }: MessageProps) {
  const resolvedVariant = variant ?? DEFAULT_VARIANT_MAP[direction]

  const { owner, time, headerImage, content, bottomContent, footer } = useMemo(() => {
    return React.Children.toArray(children).reduce(
      (acc, child) => {
        if (!React.isValidElement(child)) {
          return acc
        }

        if (child.type === MessageOwner) {
          acc.owner = child
        } else if (child.type === MessageTime) {
          acc.time = child
        } else if (
          child.type === MessageHeaderImage ||
          child.type === MessageHeaderImagesCarousel
        ) {
          acc.headerImage = child
        } else if (child.type === MessageBottomContent) {
          acc.bottomContent = child
        } else if (child.type === MessageFooter) {
          acc.footer = child
        } else if (
          child.props &&
          typeof child.props === 'object' &&
          'children' in child.props &&
          child.props.children
        ) {
          acc.content.push(child)
        }
        return acc
      },
      {
        headerImage: undefined as React.ReactNode | undefined,
        bottomContent: undefined as React.ReactNode | undefined,
        owner: undefined as React.ReactNode | undefined,
        time: undefined as React.ReactNode | undefined,
        content: [] as React.ReactNode[],
        footer: undefined as React.ReactNode | undefined,
      },
    )
  }, [children])

  return (
    <div className={buildMessageClassName({ direction })}>
      <Card
        className={cn(
          'w-fit',
          buildMessageCardClassName({
            variant: resolvedVariant,
            headerImage: !!headerImage && !content.length,
            headerImageAndContent: !!headerImage && !!content.length,
          }),
          className,
        )}
        {...props}
      >
        {headerImage}
        {!!owner && <CardTitle className="px-4 gap-0">{owner}</CardTitle>}
        {!!content.length && (
          <CardContent className={cn('px-4', buildTextAlignClassName({ direction }))}>
            {content}
          </CardContent>
        )}
        {footer}
      </Card>
      {bottomContent}
      {!!time && <div className={buildTextAlignClassName({ direction })}>{time}</div>}
    </div>
  )
}

type MessageOwnerProps = React.ComponentProps<'div'> & {
  picture?: React.ReactElement
}

function MessageOwner({ children, picture, className, ...props }: MessageOwnerProps) {
  return (
    <div className={cn('flex flex-row gap-2 items-center', className)} {...props}>
      {!!picture && <Avatar className="size-6 p-1 bg-background">{picture}</Avatar>}{' '}
      <span>{children}</span>
    </div>
  )
}

type MessageDateProps = Omit<React.ComponentProps<'div'>, 'children'> & {
  children: number
}

function MessageTime({ children, className, ...props }: MessageDateProps) {
  const isToday = new Date(children).toDateString() === new Date().toDateString()

  return (
    <div className={cn('text-sm px-4 text-tertiary', className)} {...props}>
      {isToday ? (
        <FormattedTime value={children} />
      ) : (
        <FormattedDate
          value={children}
          day="numeric"
          month="short"
          hour="numeric"
          minute="numeric"
        />
      )}
    </div>
  )
}

type MessageHeaderImageProps = React.ComponentProps<'img'>

function MessageHeaderImage({ className, ...props }: MessageHeaderImageProps) {
  return <img className={cn('max-w-fit w-full', className)} {...props} />
}

type MessageHeaderImagesCarouselProps = Omit<React.ComponentProps<typeof Carousel>, 'children'> & {
  images: string[]
  alt?: string
}

function MessageHeaderImagesCarousel({
  images,
  alt,
  className,
  ...props
}: MessageHeaderImagesCarouselProps) {
  return (
    <Carousel className={cn('w-full', className)} {...props}>
      <CarouselContent>
        {images.map(image => (
          <CarouselItem key={image}>
            <MessageHeaderImage src={image} alt={alt} className="aspect-square object-cover" />
          </CarouselItem>
        ))}
      </CarouselContent>
      {images.length > 1 && (
        <>
          <CarouselPrevious />
          <CarouselNext />
        </>
      )}
    </Carousel>
  )
}

function MessageFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return <div className={cn('px-4', className)} {...props}></div>
}

function MessageBottomContent(props: React.ComponentProps<'div'>) {
  return <div {...props}></div>
}

export {
  Message,
  MessageBottomContent,
  MessageFooter,
  MessageHeaderImage,
  MessageHeaderImagesCarousel,
  MessageOwner,
  Messages,
  MessageTime,
}
